import { useState } from "react";
import * as React from "react";
import { Pencil, X } from "lucide-react";
import {
    useRulesFunctionData,
    useCreateRuleFunction,
    useUpdateRuleFunction,
    useDeleteRuleFunction,
    useApproveRuleFunction,
    useGetFilterRuleFuction,
} from "@/hooks/useLocalStorage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Plus,
    Edit,
    Trash2,
    TreePine,
    List,
    Loader2,
    Check,
} from "lucide-react";
import {
    Code,
    insertRuleFunctionSchema,
    InsertRuleFunction,
    RuleExpression,
    RuleExpressionFilter
} from "../../shared/schema";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Textarea } from "../ui/textarea";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { FilterTypeSearch } from "@/widgets/searchFilterType";

interface HierarchicalNodeProps {
    item: RuleExpression;
    level?: number;
}

interface TableHierarchicalNodeProps {
    topLevelTypesData: RuleExpression[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (code: number) => void;
}

interface PopupProps {
    popupName: string;
    heading: RuleExpression | undefined;
    content: string | undefined;
    onClose: () => void;
}

interface Params {
    Id: number;
    FunctionId?: number;
    ParamType?: string;
    ParamName: string;
}

function CreateRulesFunctionDialog({
    trigger,
    editData,
    isEdit = false,
    open,
    setOpen,
    onSuccess
}: {
    trigger?: React.ReactNode;
    editData?: RuleExpression;
    isEdit?: boolean;
    open: boolean,
    setOpen: (value: boolean) => void
    onSuccess?: (status: boolean, message: string) => void;
}) {
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [parameters, setParameters] = useState<
        { id: number; type: string; name: string; isEditing: boolean }[]
    >([]);
    const [addWarning, setAddWarning] = useState<string>("")

    const createRuleFunctionMutation = useCreateRuleFunction();
    const updateRuleFunctionMutation = useUpdateRuleFunction();

    const handleParamChange = (index: number, key: "type" | "name", value: string) => {
        const updated = [...parameters];
        updated[index][key] = value;
        setParameters(updated);
    };

    const handleAddParam = (index: number) => {
        const updated = [...parameters];
        if (!updated[index].type || !updated[index].name.trim()) return;
        updated[index].isEditing = false;
        setParameters(updated);
    };

    const handleCancelParam = (index: number) => {
        const updated = [...parameters];
        updated.splice(index, 1);
        setParameters(updated);
    };

    const handleEditParam = (index: number) => {
        const updated = [...parameters];
        updated[index].isEditing = true;
        setParameters(updated);
    };

    const handleDeleteParam = (index: number) => {
        const updated = [...parameters];
        updated.splice(index, 1);
        setParameters(updated);
    };

    const form = useForm<InsertRuleFunction>({
        resolver: zodResolver(insertRuleFunctionSchema),
        defaultValues: {
            FunctionName: "",
            Description: "",
            Example: "",
        },
    });

    React.useEffect(() => {
        if (isEdit && editData) {
            form.reset({
                FunctionName: editData.FunctionName || "",
                Description: editData.Description || "",
                Example: editData.Example || "",
            });

            if (editData.params) {
                setParameters(
                    editData.params.map((p: any) => ({
                        id: p.Id,
                        type: p.ParamType,
                        name: p.ParamName,
                        isEditing: false,
                    }))
                );
            }
        }
    }, [isEdit, editData, form]);

    const onSubmit = async (data: InsertRuleFunction) => {
        if (parameters.length === 0) {
            setAddWarning("Please Add a Parameter!")
            setTimeout(() => setAddWarning(""), 2000);
            return
        }
        try {
            const payload = {
                FunctionName: data.FunctionName,
                Description: data.Description,
                Example: data.Example,
                params: parameters.map((param, index) => ({
                    ParamType: param.type,
                    ParamName: param.name,
                    Id: param.id || index + 1,
                })),
            };

            if (isEdit && editData) {
                const updatePayload = {
                    id: editData.Id,
                    updates: payload,
                };
                updateRuleFunctionMutation.mutateAsync(updatePayload);
            } else {
                createRuleFunctionMutation.mutateAsync(payload);

            }
        } catch (error) {
            console.error("Error saving rule:", error);
        }
    };

    React.useEffect(() => {
        if (createRuleFunctionMutation.isSuccess === true) {
            setOpen(false);
            setReqStatus(createRuleFunctionMutation.isSuccess);
            setAlertMessage(`Rule Function Added Successfully!`);
            onSuccess?.(true, "Rule Function Updated Successfully!");
            setAlertOpen(true);
            createRuleFunctionMutation.reset();
        } else if (createRuleFunctionMutation.isError) {
            const raw = createRuleFunctionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(createRuleFunctionMutation.isSuccess);
            setOpen(false);
            setAlertMessage(parsedMsg);
            onSuccess?.(true, parsedMsg);
            setAlertOpen(true);
            createRuleFunctionMutation.reset();
        }
    }, [createRuleFunctionMutation]);

    React.useEffect(() => {
        if (updateRuleFunctionMutation.isSuccess === true) {
            setOpen(false);
            setReqStatus(updateRuleFunctionMutation.isSuccess);
            setAlertMessage(`Rule Function Updated Successfully!`);
            onSuccess?.(true, "Rule Function Updated Successfully!");
            setAlertOpen(true);
            updateRuleFunctionMutation.reset();
        } else if (updateRuleFunctionMutation.isError) {
            const raw = updateRuleFunctionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(updateRuleFunctionMutation.isSuccess);
            setOpen(false);
            setAlertMessage(parsedMsg);
            onSuccess?.(true, parsedMsg);
            setAlertOpen(true);
            updateRuleFunctionMutation.reset();
        }
    }, [updateRuleFunctionMutation]);



    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Manage Function</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit, (errors) =>
                                console.log("errors", errors)
                            )}
                            className="space-y-4"
                        >

                            <FormField
                                control={form.control}
                                name="FunctionName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Function Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter Function Name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />


                            <FormField
                                control={form.control}
                                name="Description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Enter Description"
                                                {...field}
                                                className="resize-y min-h-[60px] max-h-[200px]"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="Example"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Example</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Enter Example"
                                                {...field}
                                                className="resize-y min-h-[60px] max-h-[200px]"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="mt-6">
                                <div className="mt-6">
                                    <div className="flex justify-between items-center mb-2">
                                        <FormLabel className="text-base font-medium">Function Parameters</FormLabel>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                const hasEditing = parameters.some((p) => p.isEditing)
                                                if (hasEditing) return
                                                setParameters([
                                                    ...parameters,
                                                    {
                                                        id: parameters.length + 1,
                                                        type: "",
                                                        name: "",
                                                        isEditing: true,
                                                    },
                                                ])
                                            }}
                                            disabled={parameters.some((p) => p.isEditing)}
                                        >
                                            <Plus className="h-4 w-4 mr-1" /> Add Parameter
                                        </Button>
                                    </div>

                                    <div className="border border-gray-200 rounded-md">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-gray-100">
                                                    <TableHead className="w-12 text-left p-2">ID</TableHead>
                                                    <TableHead className="text-left p-2">Type</TableHead>
                                                    <TableHead className="text-left p-2">Name</TableHead>
                                                    <TableHead className="text-left p-2 w-32 text-center">Action</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                        </Table>

                                        <div className="max-h-[140px] overflow-y-auto">
                                            <Table>
                                                <TableBody>
                                                    {parameters.length > 0 ? (
                                                        parameters.map((param, index) => (
                                                            <TableRow key={param.id}>
                                                                <TableCell>{param.id}</TableCell>

                                                                <TableCell>
                                                                    {param.isEditing ? (
                                                                        <select
                                                                            className="border rounded px-2 py-1 w-full"
                                                                            value={param.type}
                                                                            onChange={(e) =>
                                                                                handleParamChange(index, "type", e.target.value)
                                                                            }
                                                                        >
                                                                            <option value="">Select Type</option>
                                                                            <option value="int">int</option>
                                                                            <option value="string">string</option>
                                                                        </select>
                                                                    ) : (
                                                                        param.type
                                                                    )}
                                                                </TableCell>

                                                                <TableCell>
                                                                    {param.isEditing ? (
                                                                        <textarea
                                                                            rows={1}
                                                                            className="border rounded px-2 py-1 w-full"
                                                                            value={param.name}
                                                                            onChange={(e) =>
                                                                                handleParamChange(index, "name", e.target.value)
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        param.name
                                                                    )}
                                                                </TableCell>

                                                                <TableCell className="text-center">
                                                                    {param.isEditing ? (
                                                                        <div className="flex justify-center gap-2">
                                                                            <Button
                                                                                size="sm"
                                                                                type="button"
                                                                                variant="outline"
                                                                                onClick={() => handleAddParam(index)}
                                                                            >
                                                                                <Check className="h-4 w-4" />
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                type="button"
                                                                                variant="outline"
                                                                                onClick={() => handleCancelParam(index)}
                                                                            >
                                                                                <X className="h-4 w-4" />
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex justify-center gap-2">
                                                                            <Button
                                                                                size="sm"
                                                                                type="button"
                                                                                variant="outline"
                                                                                onClick={() => handleEditParam(index)}
                                                                            >
                                                                                <Edit className="h-4 w-4" />
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                type="button"
                                                                                variant="outline"
                                                                                onClick={() => handleDeleteParam(index)}
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={4} className="text-center text-gray-500">
                                                                No parameters added yet
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>

                                    {parameters.some((p) => p.isEditing) && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Please save or cancel the current parameter before adding a new one.
                                        </p>
                                    )}
                                    {addWarning && (
                                        <div
                                            className="text-center text-red-600 mt-2 font-medium opacity-100 transition-opacity duration-700"
                                            style={{ opacity: addWarning ? 1 : 0 }}
                                        >
                                            {addWarning}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        form.reset({
                                            FunctionName: "",
                                            Description: "",
                                            Example: ""
                                        });
                                        setParameters([]);
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-workfinity-500 hover:bg-workfinity-600"
                                    disabled={
                                        createRuleFunctionMutation.isPending || updateRuleFunctionMutation.isPending
                                    }
                                >
                                    {createRuleFunctionMutation.isPending ||
                                        updateRuleFunctionMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {isEdit ? "Updating Rule Function..." : "Creating Rule Function..."}
                                        </>
                                    ) : isEdit ? (
                                        "Update Rule Function"
                                    ) : (
                                        "Create Rule Function"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}

function Popup({ popupName, heading, content, onClose }: PopupProps) {

    const isCode =
        typeof content === "string" &&
        (content.includes("\\n") || content.includes("{") || content.includes("}"));

    const formattedContent = isCode ? content.replace(/\\r?\\n/g, "\n") : content;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Rule {popupName}</DialogTitle>
                </DialogHeader>

                <div className="mt-4 space-y-4 text-sm text-gray-800">
                    <div>
                        <label className="font-semibold text-black">Rule Name :</label>
                        <div className="border border-gray-400 rounded-md p-2 mt-1 bg-white">
                            {heading?.FunctionName || ""}
                        </div>
                    </div>

                    <div>
                        <label className="font-semibold text-black">
                            {popupName}
                        </label>
                        <div className="min-h-[200px] border border-gray-400 rounded-md p-2 mt-1 bg-white whitespace-pre-wrap font-mono text-sm">
                            {formattedContent || ""}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TableRule({
    topLevelTypesData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<RuleExpression[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<RuleExpression | null>(null);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<RuleExpression | undefined>();
    const [popupName, setPopupName] = React.useState("");
    const [dataContent, setDataContent] = React.useState<string | undefined>("");
    const [itemToApprove, setItemToApprove] = useState<RuleExpression | null>(null);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState<RuleExpression>();

    const deleteRuleFunctionMutation = useDeleteRuleFunction();
    const approveRuleFunctionMutation = useApproveRuleFunction();

    React.useEffect(() => {
        if (topLevelTypesData) {
            setCurrentData(topLevelTypesData);
        }
    }, [topLevelTypesData]);

    function formatDate(dateString: string): string {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return "";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }

    function handleDeleteClick(e: React.MouseEvent, item: RuleExpression) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleApprove(e: React.MouseEvent, item: RuleExpression) {
        e.stopPropagation();
        setItemToApprove(item);
        setApproveDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;

        if (itemToDelete.FunctionName) {
            deleteRuleFunctionMutation.mutate({
                key: itemToDelete?.Id,
            });
        }
    }

    function handleConfirmApprove() {
        if (!itemToApprove) return;

        if (itemToApprove.FunctionName) {
            approveRuleFunctionMutation.mutate({
                key: itemToApprove?.Id,
                status: itemToApprove?.Approved,
            });
        }
    }

    const handleAlert = (status: boolean, message: string) => {
        setAlertOpen(status)
        setAlertMessage(message)
    };

    React.useEffect(() => {
        if (approveRuleFunctionMutation.isSuccess === true) {
            setApproveDialogOpen(false);
            setReqStatus(approveRuleFunctionMutation.isSuccess);
            setAlertMessage(`${itemToApprove?.Approved === 1 ? "RuleFunction UnApproved Successfully!" : "RuleFunction Approved Successfully!"}`);
            setItemToApprove(null);
            setAlertOpen(true);
            approveRuleFunctionMutation.reset();
        } else if (approveRuleFunctionMutation.isError) {
            const raw = approveRuleFunctionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(approveRuleFunctionMutation.isSuccess);
            setApproveDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            approveRuleFunctionMutation.reset();
        }
    }, [approveRuleFunctionMutation]);

    React.useEffect(() => {
        if (deleteRuleFunctionMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            setReqStatus(deleteRuleFunctionMutation.isSuccess);
            setAlertMessage(`RuleFunction deleted Successfully!`);
            setItemToDelete(null);
            setAlertOpen(true);
            deleteRuleFunctionMutation.reset();
        } else if (deleteRuleFunctionMutation.isError) {
            const raw = deleteRuleFunctionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(deleteRuleFunctionMutation.isSuccess);
            setDeleteDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            deleteRuleFunctionMutation.reset();
        }
    }, [deleteRuleFunctionMutation]);

    return (
        <div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <>
                                <TableHead>Id</TableHead>
                                <TableHead>Function Name</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Example</TableHead>
                                <TableHead>Count</TableHead>
                                <TableHead>Parameter</TableHead>
                                <TableHead>Date Created</TableHead>
                                <TableHead>Approve Status</TableHead>
                                <TableHead className="text-center">Actions</TableHead>
                                <TableHead>Configured</TableHead>
                                <TableHead>Date Modified</TableHead>
                            </>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow>
                                <TableCell className="font-mono">{data.Id}</TableCell>

                                <TableCell className="font-mono font-medium">
                                    {data.FunctionName}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <Button
                                        variant={"link"}
                                        onClick={() => {
                                            setPopupName("Description")
                                            setDataHead(data);
                                            setDataContent(data.Description);
                                            setShowPopup(true);
                                        }}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <Button
                                        variant={"link"}
                                        onClick={() => {
                                            setPopupName("Example")
                                            setDataHead(data);
                                            setDataContent(data.Example);
                                            setShowPopup(true);
                                        }}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.ParamCount}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <Button
                                        variant={"link"}
                                        onClick={() => {
                                            setPopupName("Parameter")
                                            setDataHead(data);
                                            setDataContent(data.params && data.params.length > 0
                                                ? data.params
                                                    .map((p: Params) => `(${p.ParamType} ${p.ParamName})`)
                                                    .join(", ")
                                                : "");
                                            setShowPopup(true);
                                        }}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {formatDate(data.DateTimeCreated)}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.Approved === 1 ? (
                                        <Button variant={"link"}>
                                            <Check className="h-4 w-4 mr-1 text-green-800" />
                                        </Button>
                                    ) : (
                                        <Button variant={"link"}>
                                            <X className="h-4 w-4 mr-1 text-red-800" />
                                        </Button>
                                    )}
                                </TableCell>

                                <TableCell>
                                    <div className="flex flex-row items-center space-x-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="min-w-6 h-6 p-0"
                                                    tooltip="More actions"
                                                >
                                                    <Plus className="h-3 w-3" />
                                                    <span className="sr-only">More actions</span>
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent
                                                align="end"
                                                className="min-w-[100px]"
                                            >

                                                <DropdownMenuItem
                                                    onClick={(e) => handleApprove(e, data)}
                                                    className="flex items-center px-1 cursor-pointer"
                                                >
                                                    {data.Approved === 0 ? (
                                                        <Check className="h-4 w-4 mr-1 text-green-600" />
                                                    ) : (
                                                        <X className="h-4 w-4 mr-1 text-red-600" />
                                                    )}
                                                    <span className="text-sm">
                                                        {data.Approved === 0 ? "Approve" : "Unapprove"}
                                                    </span>
                                                </DropdownMenuItem>

                                                <DropdownMenuItem
                                                    onSelect={(e) => {
                                                        e.preventDefault();
                                                        setDialogOpen(true);
                                                        setEditData(data);
                                                    }}
                                                    className="flex items-center px-1 py-2 cursor-pointer"
                                                >
                                                    <Pencil className="h-4 w-4 mr-1" /> Edit
                                                </DropdownMenuItem>

                                                <DropdownMenuItem
                                                    onClick={(e) => handleDeleteClick(e, data)}
                                                    className="flex items-center px-1 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                                                >
                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                    <span className="text-sm">
                                                        Delete
                                                    </span>
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.Configured ? (
                                        <Button variant={"link"}>
                                            <Check className="h-4 w-4 mr-1 text-green-800" />
                                        </Button>
                                    ) : (
                                        <Button variant={"link"}>
                                            <X className="h-4 w-4 mr-1 text-red-800" />
                                        </Button>
                                    )}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {formatDate(data.ModifiedDate)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <PaginationControl
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onPageChange={onPageChange}
                />
            </div>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={deleteRuleFunctionMutation.isPending}
                itemName={(itemToDelete as RuleExpression)?.FunctionName}
                itemType={"RuleFunction"}
            />

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

            <ConfirmationDialog
                open={approveDialogOpen}
                onOpenChange={setApproveDialogOpen}
                onConfirm={handleConfirmApprove}
                isPending={approveRuleFunctionMutation.isPending}
                heading={
                    itemToApprove?.Approved === 1
                        ? "Confirm UnApprove Request"
                        : "Confirm Approve Request"
                }
                message={`Are you sure you want to ${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"
                    } this RuleFunction?`}
                confirmLabel={`${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"
                    }`}
            // cancelLabel="Cancel"
            />

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {dialogOpen &&
                <CreateRulesFunctionDialog
                    isEdit={true}
                    editData={editData}
                    open={dialogOpen}
                    setOpen={setDialogOpen}
                    onSuccess={handleAlert}
                />
            }

        </div>
    );
}

function RuleHierarchicalNode({
    item,
    level = 0,
}: HierarchicalNodeProps) {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<RuleExpression | null>(null);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<RuleExpression | undefined>();
    const [dataContent, setDataContent] = React.useState<string | undefined>("");
    const [itemToApprove, setItemToApprove] = useState<RuleExpression | null>(null);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const deleteRuleFunctionMutation = useDeleteRuleFunction();
    const approveRuleFunctionMutation = useApproveRuleFunction();

    function handleDeleteClick(e: React.MouseEvent, item: RuleExpression) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleApprove(e: React.MouseEvent, item: RuleExpression) {
        e.stopPropagation();
        setItemToApprove(item);
        setApproveDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;

        if (itemToDelete.FunctionName) {
            deleteRuleFunctionMutation.mutate({
                key: itemToDelete?.Id,
            });
        }
    }

    function handleConfirmApprove() {
        if (!itemToApprove) return;

        if (itemToApprove.FunctionName) {
            approveRuleFunctionMutation.mutate({
                key: itemToApprove?.Id,
                status: itemToApprove?.Approved,
            });
        }
    }

    React.useEffect(() => {
        if (approveRuleFunctionMutation.isSuccess === true) {
            setApproveDialogOpen(false);
            setReqStatus(approveRuleFunctionMutation.isSuccess);
            setAlertMessage(`${itemToApprove?.Approved === 1 ? "RuleFunction UnApproved Successfully!" : "RuleFunction Approved Successfully!"}`);
            setItemToApprove(null);
            setAlertOpen(true);
            approveRuleFunctionMutation.reset();
        } else if (approveRuleFunctionMutation.isError) {
            const raw = approveRuleFunctionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(approveRuleFunctionMutation.isSuccess);
            setApproveDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            approveRuleFunctionMutation.reset();
        }
    }, [approveRuleFunctionMutation]);

    React.useEffect(() => {
        if (deleteRuleFunctionMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            setReqStatus(deleteRuleFunctionMutation.isSuccess);
            setAlertMessage(`RuleFunction deleted Successfully!`);
            setItemToDelete(null);
            setAlertOpen(true);
            deleteRuleFunctionMutation.reset();
        } else if (deleteRuleFunctionMutation.isError) {
            const raw = deleteRuleFunctionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(deleteRuleFunctionMutation.isSuccess);
            setDeleteDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            deleteRuleFunctionMutation.reset();
        }
    }, [deleteRuleFunctionMutation]);

    return (
        <div className="border bg-white border-gray-200 rounded-lg">
            <div
                className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 hover:bg-gray-50 cursor-pointer"
                style={{ paddingLeft: `${8 + level * 16}px` }}
            >

                <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 ml-4">
                        <span className="font-medium text-gray-900 truncate text-sm md:text-base">
                            {(item as RuleExpression).FunctionName}
                        </span>
                        <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                            {(item as RuleExpression).Id}
                        </span>
                    </div>
                    {item?.params?.length! > 0 && (
                        <div className="text-xs md:text-sm text-gray-500 truncate mt-1 ">
                            <Button
                                variant={"link"}
                                onClick={() => {
                                    setDataHead(item);
                                    setDataContent(item.params && item.params.length > 0
                                        ? item.params
                                            .map((p: Params) => `(${p.ParamType} ${p.ParamName})`)
                                            .join(", ")
                                        : "");
                                    setShowPopup(true);
                                }}
                            >
                                View Parameter
                            </Button>
                        </div>
                    )}
                </div>

                <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                    {item.Approved !== 0 ? (
                        <Button onClick={(e) => handleApprove(e, item)} tooltip="Approve" variant={"link"}>
                            <Check className="h-4 w-4 mr-1 text-green-800" />
                        </Button>
                    ) : (
                        <Button onClick={(e) => handleApprove(e, item)} tooltip="UnApprove" variant={"link"}>
                            <X className="h-4 w-4 mr-1 text-red-800" />
                        </Button>
                    )}

                    <CreateRulesFunctionDialog
                        isEdit={true}
                        editData={item}
                        open={addDialogOpen}
                        setOpen={setAddDialogOpen}
                        onSuccess={(status, message) => {
                            console.log(status, message);
                        }}
                        trigger={
                            <Button
                                tooltip="Edit RuleFunction"
                                size="sm"
                                variant="ghost"
                                className="w-7 h-7 md:w-8 md:h-8 p-0"
                                onClick={() => setAddDialogOpen(true)}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        }
                    />

                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleDeleteClick(e, item)}
                        className="text-red-600"
                        tooltip="Delete RuleFunction"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={deleteRuleFunctionMutation.isPending}
                itemName={(itemToDelete as RuleExpression)?.FunctionName}
                itemType={"RuleFunction"}
            />

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

            <ConfirmationDialog
                open={approveDialogOpen}
                onOpenChange={setApproveDialogOpen}
                onConfirm={handleConfirmApprove}
                isPending={approveRuleFunctionMutation.isPending}
                heading={
                    itemToApprove?.Approved === 1
                        ? "Confirm UnApprove Request"
                        : "Confirm Approve Request"
                }
                message={`Are you sure you want to ${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"
                    } this RuleFunction?`}
                confirmLabel={`${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"
                    }`}
            // cancelLabel="Cancel"
            />

            {showPopup && (
                <Popup
                    popupName="Parameter"
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                />
            )}
        </div>
    );
}

export function RulesFunction() {
    const [viewMode, setViewMode] = useState<"table" | "tree">("table");
    const [topLevelTypes, setTopLevelTypes] = useState<RuleExpression[]>([]);

    const [typeInput, setTypeInput] = useState<string>("ALL");
    const [typeFilter, setTypeFilter] = useState<string>("ALL");
    const [filterOpen, setFilterOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(10);

    const [filterName, setFilterName] = useState<string>("");
    const [filterTypeData, setFilterTypeData] = useState<RuleExpressionFilter[]>([]);

    const { data: types, isLoading } = useRulesFunctionData(
        pageIndex,
        pageSize,
        typeFilter === "ALL" ? undefined : typeFilter
    );

    const { data: filterTypes, isFetching: isFilterFetching } = useGetFilterRuleFuction(
        filterName.length >= 3 ? filterName : undefined,
    );

    React.useEffect(() => {
        setTopLevelTypes(types?.items ?? []);
        setTotalCount(types?.total ?? 0);
    }, [types]);

    React.useEffect(() => {
        if (filterTypes && filterTypes.length > 0) {
            setFilterTypeData(filterTypes)
        } else {
            setFilterTypeData([])
        }
    }, [filterTypes])

    React.useEffect(() => {
        const selectedItemStr = localStorage.getItem("cm:selectedItem");
        if (selectedItemStr) {
            try {
                const selectedItem = JSON.parse(selectedItemStr);
                console.log("Navigate to item in TreeExplorer:", selectedItem);
                // Clear the selection after handling
                localStorage.removeItem("cm:selectedItem");

                // TODO: Expand tree to show the selected item
                // This could involve expanding parent nodes and highlighting the target
            } catch (error) {
                console.error("Error parsing selected item:", error);
                localStorage.removeItem("cm:selectedItem");
            }
        }
    }, []);

    const handleSearch = () => {
        setTypeFilter(typeInput);
    };

    const handleRefresh = (item: boolean) => {
        setTypeInput("ALL");
        setTypeFilter("ALL");
        if (item) setFilterOpen(false);
    };

    return (
        <div className="bg-gray-50 min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                            Rules Function
                        </h2>
                        <p className="text-gray-600 mt-1 text-sm md:text-base">
                            Manage & Create Function for Rules
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <Button
                                size="sm"
                                variant={viewMode === "table" ? "default" : "ghost"}
                                onClick={() => setViewMode("table")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <List className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Table</span>
                                <span className="sm:hidden">Table</span>
                            </Button>
                            <Button
                                size="sm"
                                variant={viewMode === "tree" ? "default" : "ghost"}
                                onClick={() => setViewMode("tree")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <TreePine className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Tree</span>
                                <span className="sm:hidden">Tree</span>
                            </Button>
                        </div>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen(true)}
                        >
                            Filter Function
                        </Button>
                        <CreateRulesFunctionDialog
                            open={addDialogOpen}
                            setOpen={setAddDialogOpen}
                            onSuccess={(status, message) => {
                                console.log(status, message);
                            }}
                            trigger={
                                <Button
                                    className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                                    size="sm"
                                    onClick={() => setAddDialogOpen(true)}
                                >
                                    <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                                    Add Function
                                </Button>
                            }
                        />
                    </div>
                </div>
            </div>

            <div>
                {filterOpen &&
                    <Card className="mb-4 md:mb-6">
                        <CardContent className="pt-0">
                            <div className="w-[450px]">
                                <FilterTypeSearch
                                    filterTypeData={filterTypeData}
                                    isLoading={isFilterFetching}
                                    value={typeInput}
                                    onChange={setTypeInput}
                                    onSearch={(query) => setFilterName(query)}
                                />
                            </div>

                            <div className="mt-4 flex justify-center space-x-2">
                                <Button onClick={handleSearch}>Search</Button>
                                <Button variant="outline" onClick={() => handleRefresh(false)}>
                                    Refresh
                                </Button>
                                <Button variant="outline" onClick={() => handleRefresh(true)}>
                                    Close
                                </Button>
                            </div>

                        </CardContent>
                    </Card>
                }
            </div>

            {!isLoading ? (
                viewMode === "table" ? (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    <>
                                        <TableRule
                                            topLevelTypesData={topLevelTypes}
                                            pageIndex={pageIndex}
                                            pageSize={pageSize}
                                            totalCount={totalCount}
                                            onPageChange={setPageIndex}
                                        />
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No Rule Function found. Create to get started.</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    topLevelTypes.map((type) => (
                                        <RuleHierarchicalNode
                                            key={type.Id}
                                            item={type}
                                        />
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No Rule Function found! Create to get started.</p>
                                    </div>
                                )}
                                <PaginationControl
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                />
                            </div>
                        </CardContent>
                    </Card>
                )
            ) : (
                <div className="p-6">Loading...</div>
            )}
        </div>
    );
}
